import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import ProGrid from "../../Components/ProGrid/v2";
import Layout from "../../Components/Layout";
import { STATUS_MAPPINGS, STAUTES } from "../../../utils/constant";
import { errorToast } from "../../../utils/toastHelper";
import usePageState from "../../../utils/customHooks/usePageState";
import { getApi } from "../../../utils/services";
import useColumn from "../../../utils/customHooks/useColumns";
import { EMPLOYEES, APPRENTICE_FORM_AND_DOCUMENT_STATUS, DOWNLOAD } from "../../../utils/services/apiPath";
import IconButton from "@mui/material/IconButton";
import GestureIcon from "@mui/icons-material/Gesture";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { useSelector } from "react-redux";
import { loginSelector } from "../../store/features/auth/authSlice";
import RegistrationPendingModel from "../../Components/RegistrationPendingModel";
import SignDocumentModel from "../../Components/SignDocumentModel";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { createODataQuery } from "../../../utils/services/oData";

export default function ESignatureList() {
  const {
    currentPage,
    pageSize,
    data: clientsData,
    rowCount,
    status,
    searchString,
    setPageState,
    setPagination,
    setStatus,
    setSortModel,
    setSearchString
  } = usePageState();

  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [openRegistrationModal, setOpenRegistrationModal] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [pendingItems, setPendingItems] = useState([]);
  const [modalText, setModalText] = useState("");
  const { user } = useSelector(loginSelector);
  const navigate = useNavigate();

  const columns = useColumn(
    [
      {
        field: "formName",
        headerName: "FormName"
      },
      {
        field: "createdDate",
        headerName: "CreatedDate"
      },
      {
        field: "status",
        headerName: "ESignedStatus"
      },
      {
        field: "firstSignerSharedLink",
        headerName: "Actions",
        renderCell: params => (
          <>
            {params.row.status === "Completed" ? (
              <Tooltip title={"Download"} arrow>
                <IconButton onClick={() => handleDownloadDocument(params.row.documentsKey)} color="primary">
                  <FileDownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title={params.row.isApprenticeSigned ? "View Document" : "Sign"} arrow>
                <IconButton onClick={() => handleSignDocument(params.value)} color="primary">
                  {params.row.isApprenticeSigned ? <VisibilityIcon fontSize="small" /> : <GestureIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            )}
          </>
        )
      }
    ],
    true
  );

  const getAllDocuments = useCallback(async () => {
    setStatus(STAUTES.LOADING);
    const payload = {
      pageIndex: currentPage + 1,
      pageSize: pageSize,
      orderByAscending: false,
      searchString: ""
    };
    const searchableFields = ["formName", "status"];
    const oDataQuery = createODataQuery(searchableFields, [], "", searchString, "", pageSize, currentPage);
    const { value, totalRecords, error } = await getApi(`${EMPLOYEES}/${user.employeeId}/formDocuments${oDataQuery}`, payload);
    setStatus(STAUTES.IDLE);
    if (error) return errorToast(error);
    setPageState(prevPageInfo => ({ ...prevPageInfo, data: value || [], rowCount: totalRecords }));
  }, [currentPage, pageSize, searchString, setPageState, setStatus]);

  const handleDownloadDocument = async documentsKey => {
    const pdfUrl = await getApi(`${DOWNLOAD}?documentsKey=${documentsKey}`);
    const link = document.createElement("a");
    link.href = pdfUrl;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const onClose = () => {
    setOpen(false);
  };

  const handleSignDocument = formLink => {
    setSessionId(formLink);
    setOpen(true);
  };

  const handleProfileUpdate = async () => {
    try {
      const { documentStatus, isFormCompleted } = await getApi(`${APPRENTICE_FORM_AND_DOCUMENT_STATUS}/${user.employeeId}`);
      const tempPendingItems = Object.entries(STATUS_MAPPINGS)
        .filter(([key]) => documentStatus?.includes(key))
        .map(([, value]) => t(value));
      setPendingItems(tempPendingItems);
      const modalText =
        documentStatus?.length === 2 && documentStatus?.includes("RAP_NOT_ASSIGNED")
          ? t("ApprenticeRegistrationRAPPendingText")
          : t("ApprenticeRegistrationPendingText");
      setModalText(modalText);
      if (!isFormCompleted) setOpenRegistrationModal(true);
    } catch (error) {
      errorToast(error);
    }
  };

  useEffect(() => {
    getAllDocuments();
    handleProfileUpdate();
  }, [getAllDocuments]);

  return (
    <>
      <Layout>
        <ProGrid
          title={t("Documents")}
          loading={status === STAUTES.LOADING}
          options={{
            getRowId: row => row.documentId,
            rowCount: rowCount,
            paginationMode: "server",
            paginationModel: { pageSize: pageSize, page: currentPage },
            onPaginationModelChange: setPagination,
            sortingMode: "server",
            onSortModelChange: setSortModel
          }}
          columns={columns}
          hideAddButton={true}
          hideExport={false}
          rows={clientsData || []}
          searchMode={!!searchString}
          searchModel={{ defaultValue: searchString, handleDebounce: setSearchString }}
        />
      </Layout>
      {user?.isApprentice && (
        <>
          <RegistrationPendingModel
            open={openRegistrationModal}
            title={t("ApprenticeRegistrationPendingTitle")}
            message={modalText}
            pendingItems={pendingItems}
            onConfirm={() => navigate("/registration")}
            confirmBtnText={t("Ok")}
          />
          <SignDocumentModel open={open} title={t("SignDocument")} formLink={sessionId} handleClose={onClose} />
        </>
      )}
    </>
  );
}
